import { CustomPanel } from "@finanedi.es/storybook"
import {
  faAngleDown,
  faAngleRight,
  faTimes,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { TFunction } from "i18next"
import { useState } from "react"
import { FieldErrors, UseFormSetValue } from "react-hook-form"
import { getProvinceIneOptions } from "utils/provinceDropdownOptions"
import {
  FieldTemplateGeneric,
  FieldTemplateThree,
  TEMPLATE_TYPE,
  renderFieldTemplateGeneric,
  renderFieldTemplateThree,
} from "../../../PlantillasBloquesTabla"
import { Modelo347Form } from "../DeclaracionModelo347"

const DeclaradosTemplate = ({
  t,
  text,
  values,
  setValue,
  declaradoIndex,
  handleDelete,
  errors,
}: {
  t: TFunction
  text: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  declaradoIndex: number
  handleDelete: () => void
  errors: FieldErrors<Modelo347Form>
}) => {
  const [categoryExpanded, setCategoryExpanded] = useState(true)
  const fieldsDeclarados: FieldTemplateGeneric[] = [
    {
      id: "row1",
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.nifDeclarado"),
          code: `declarados.${declaradoIndex}.nif`,
          value: values?.declarados?.[declaradoIndex]?.nif,
          inputType: "text",
        },
        {
          label: t("declaracionModelo347.declarados.nifIva"),
          code: `declarados.${declaradoIndex}.nifOperadorComunitario`,
          value: values?.declarados?.[declaradoIndex]?.nifOperadorComunitario,
          inputType: "text",
        },
        {
          label: t("declaracionModelo347.declarados.nifRepresentante"),
          code: `declarados.${declaradoIndex}.nifRepresentanteLegal`,
          value: values?.declarados?.[declaradoIndex]?.nifRepresentanteLegal,
          inputType: "text",
        },
      ],
    },
    {
      id: "row2",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.name"),
          code: `declarados.${declaradoIndex}.denominacionDeclarado`,
          value: values?.declarados?.[declaradoIndex]?.denominacionDeclarado,
          inputType: "text",
          colSpan: "tw-col-span-4",
          mandatory: true,
          error:
            errors?.declarados?.[declaradoIndex]?.denominacionDeclarado
              ?.message,
        },
      ],
    },
    {
      id: "row3",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.codigoProvincia"),
          code: `declarados.${declaradoIndex}.provincia`,
          value: values?.declarados?.[declaradoIndex]?.provincia,
          inputType: "dropdown",
          options: getProvinceIneOptions(t),
          colSpan: "tw-col-span-2",
        },
        {
          label: t("taxSummary.declaracion.codigoPais"),
          code: `declarados.${declaradoIndex}.codigoPais`,
          value: values?.declarados?.[declaradoIndex]?.codigoPais,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row4",
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.claveOperacion"),
          code: `declarados.${declaradoIndex}.clave`,
          value: values?.declarados?.[declaradoIndex]?.clave,
          inputType: "text",
        },
        {
          label: t("declaracionModelo347.declarados.opSeguro"),
          code: `declarados.${declaradoIndex}.esOperacionSeguro`,
          value: values?.declarados?.[declaradoIndex]?.esOperacionSeguro,
          inputType: "checkbox",
          disabled: Boolean(
            values?.declarados?.[declaradoIndex]?.esOperacionNegocio
          ),
        },
        {
          label: t("declaracionModelo347.declarados.arrendamientoLocalNegocio"),
          code: `declarados.${declaradoIndex}.esOperacionNegocio`,
          value: values?.declarados?.[declaradoIndex]?.esOperacionNegocio,
          inputType: "checkbox",
          disabled: Boolean(
            values?.declarados?.[declaradoIndex]?.esOperacionSeguro
          ),
        },
      ],
    },
    {
      id: "row5",
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.opIvaCaja"),
          code: `declarados.${declaradoIndex}.esOperacionIvaCaja`,
          value: values?.declarados?.[declaradoIndex]?.esOperacionIvaCaja,
          inputType: "checkbox",
          disabled: Boolean(
            values?.declarados?.[declaradoIndex]?.esOperacionIsp ||
              values?.declarados?.[declaradoIndex]?.esOperacionAduanero
          ),
        },
        {
          label: t("declaracionModelo347.declarados.opConIsp"),
          code: `declarados.${declaradoIndex}.esOperacionIsp`,
          value: values?.declarados?.[declaradoIndex]?.esOperacionIsp,
          inputType: "checkbox",
          disabled: Boolean(
            values?.declarados?.[declaradoIndex]?.esOperacionIvaCaja ||
              values?.declarados?.[declaradoIndex]?.esOperacionAduanero
          ),
        },
        {
          label: t("declaracionModelo347.declarados.opRegDepDistintoAduanero"),
          code: `declarados.${declaradoIndex}.esOperacionAduanero`,
          value: values?.declarados?.[declaradoIndex]?.esOperacionAduanero,
          inputType: "checkbox",
          disabled: Boolean(
            values?.declarados?.[declaradoIndex]?.esOperacionIvaCaja ||
              values?.declarados?.[declaradoIndex]?.esOperacionIsp
          ),
        },
      ],
    },
    {
      id: "row6",
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importePercibidoMetalico"),
          code: `declarados.${declaradoIndex}.importePercibidoMetalico`,
          value: values?.declarados?.[declaradoIndex]?.importePercibidoMetalico,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
        {
          label: t("taxSummary.declaracion.ejercicio"),
          code: `declarados.${declaradoIndex}.ejercicio`,
          value: values?.declarados?.[declaradoIndex]?.ejercicio,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row7",
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importeAnual"),
          code: `declarados.${declaradoIndex}.importeAnual`,
          value: values?.declarados?.[declaradoIndex]?.importeAnual,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row8",
      inputsOne: [
        {
          label: t(
            "declaracionModelo347.declarados.importeAnualTransmisionesInmuebles"
          ),
          code: `declarados.${declaradoIndex}.importeAnualPercibidoTransmisionesInmueblesSujetasIva`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importeAnualPercibidoTransmisionesInmueblesSujetasIva,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
        {
          label: t(
            "declaracionModelo347.declarados.importeAnualOperacionesIvaCaja"
          ),
          code: `declarados.${declaradoIndex}.importeAnualOperacionesDevengadasCriterioCajaIva`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importeAnualOperacionesDevengadasCriterioCajaIva,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
  ]

  const trimestresDeclarados: FieldTemplateThree[] = [
    {
      section: t("taxSummary.firstTrimester"),
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importeOperaciones"),
          code: `declarados.${declaradoIndex}.importePrimerTrimestre`,
          value: values?.declarados?.[declaradoIndex]?.importePrimerTrimestre,
          inputType: "currency",
        },
        {
          label: t(
            "declaracionModelo347.declarados.importeTransmisionesInmuebles"
          ),
          code: `declarados.${declaradoIndex}.importePercibidoTransmisionesInmueblesSujetasIvaPrimerTrimestre`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importePercibidoTransmisionesInmueblesSujetasIvaPrimerTrimestre,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      section: t("taxSummary.secondTrimester"),
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importeOperaciones"),
          code: `declarados.${declaradoIndex}.importeSegundoTrimestre`,
          value: values?.declarados?.[declaradoIndex]?.importeSegundoTrimestre,
          inputType: "currency",
        },
        {
          label: t(
            "declaracionModelo347.declarados.importeTransmisionesInmuebles"
          ),
          code: `declarados.${declaradoIndex}.importePercibidoTransmisionesInmueblesSujetasIvaSegundoTrimestre`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importePercibidoTransmisionesInmueblesSujetasIvaSegundoTrimestre,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      section: t("taxSummary.thirdTrimester"),
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importeOperaciones"),
          code: `declarados.${declaradoIndex}.importeTercerTrimestre`,
          value: values?.declarados?.[declaradoIndex]?.importeTercerTrimestre,
          inputType: "currency",
        },
        {
          label: t(
            "declaracionModelo347.declarados.importeTransmisionesInmuebles"
          ),
          code: `declarados.${declaradoIndex}.importePercibidoTransmisionesInmueblesSujetasIvaTercerTrimestre`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importePercibidoTransmisionesInmueblesSujetasIvaTercerTrimestre,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      section: t("taxSummary.fourthTrimester"),
      inputsOne: [
        {
          label: t("declaracionModelo347.declarados.importeOperaciones"),
          code: `declarados.${declaradoIndex}.importeCuartoTrimestre`,
          value: values?.declarados?.[declaradoIndex]?.importeCuartoTrimestre,
          inputType: "currency",
        },
        {
          label: t(
            "declaracionModelo347.declarados.importeTransmisionesInmuebles"
          ),
          code: `declarados.${declaradoIndex}.importePercibidoTransmisionesInmueblesSujetasIvaCuartoTrimestre`,
          value:
            values?.declarados?.[declaradoIndex]
              ?.importePercibidoTransmisionesInmueblesSujetasIvaCuartoTrimestre,
          inputType: "currency",
          colSpan: "tw-col-span-2",
        },
      ],
    },
  ]

  return (
    <CustomPanel
      title={text}
      expandIcon={null}
      collapseIcon={null}
      className='tw-cursor-pointer tw-font-medium !tw-shadow-none tw-border [&>div:nth-child(2)]:!tw-max-h-[3000px]'
      collapsed={categoryExpanded}
      onToggle={(e) => setCategoryExpanded(e.value)}
      headerTemplate={() => (
        <div
          className='tw-flex tw-justify-between tw-px-4 tw-py-3'
          onClick={() => setCategoryExpanded((prevState) => !prevState)}
        >
          <p>{text}</p>
          <div>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
              className='hover:tw-bg-gray-100 tw-w-6 tw-rounded tw-transition-colors'
            >
              <FontAwesomeIcon
                icon={faTimes}
                className='tw-text-red-500'
              />
            </button>
            <button
              type='button'
              onClick={() => setCategoryExpanded((prevState) => !prevState)}
              className='tw-w-4 tw-ml-4'
            >
              <FontAwesomeIcon
                icon={categoryExpanded ? faAngleDown : faAngleRight}
              />
            </button>
          </div>
        </div>
      )}
    >
      <div className='tw-p-4'>
        {fieldsDeclarados.map((row) => (
          <div
            key={row.id}
            className='tw-w-full tw-h-fit tw-grid tw-grid-cols-4 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'
          >
            {renderFieldTemplateGeneric({
              fields: row.inputsOne,
              setValue,
              t,
            })}
          </div>
        ))}
        {trimestresDeclarados.map((field) =>
          renderFieldTemplateThree(
            field,
            setValue,
            TEMPLATE_TYPE.TEMPLATE_FORMAT_TWO,
            "tw-grid-cols-4",
            "",
            "tw-col-span-1"
          )
        )}
      </div>
    </CustomPanel>
  )
}

export default DeclaradosTemplate
